import { IconChevronsLeft, IconChevronsRight } from "@/assets/icons";
import { isNumber } from "@/utils/number";
import { Pagination as MuiPagination, PaginationItem, Stack, styled } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

type Props = {
  totalPage?: number;
  count?: number;
  // When provided, pagination becomes controlled (local state) instead of URL-driven.
  // Used for modal contexts (e.g. MatchingResultsModal) where URL params are not desired.
  page?: number;
  onPageChange?: (page: number) => void;
  // Disables all page controls (e.g. while a ledger is in unsaved edit mode).
  disabled?: boolean;
};

export const Pagination: FC<Props> = ({ totalPage, count, page: controlledPage, onPageChange, disabled }) => {
  const { pathname, search } = useLocation();
  const isControlled = onPageChange !== undefined;

  const query = new URLSearchParams(location.search);
  const urlPage = parseInt(query.get("page") || "1");
  const page = isControlled ? (controlledPage ?? 1) : urlPage;

  const [displayTotalPage, setDisplayTotalPage] = useState<number>(1);

  useEffect(() => {
    if (isNumber(totalPage)) {
      setDisplayTotalPage(totalPage > 0 ? totalPage : 1);
    }
  }, [totalPage]);

  return (
    <Stack direction="row" alignItems="center" gap={2.5}>
      <StyledPagination
        page={page}
        count={totalPage}
        disabled={disabled}
        variant="outlined"
        shape="rounded"
        showFirstButton
        showLastButton
        renderItem={(item) => {
          // Controlled (callback) variant - button-based, no URL changes
          if (isControlled) {
            return (
              <PaginationItem
                component="button"
                slots={{ first: IconChevronsLeft, last: IconChevronsRight }}
                {...item}
                onClick={() => {
                  if (typeof item.page === "number") {
                    onPageChange(item.page);
                  }
                }}
              />
            );
          }

          // URL-bound variant (default) - preserves existing callers' behavior
          const searchParams = new URLSearchParams(search);
          searchParams.set("page", item.page?.toString() ?? "1");

          return (
            <PaginationItem
              component={Link}
              to={{ pathname, search: searchParams.toString() }}
              slots={{ first: IconChevronsLeft, last: IconChevronsRight }}
              {...item}
            />
          );
        }}
      />
    </Stack>
  );
};

const StyledPagination = styled(MuiPagination)(() => ({
  "& .MuiPaginationItem-root": {
    border: "2px solid #cfd4d9",
    borderRadius: 8,
    minWidth: 40,
    height: 40,
    fontSize: 14,
    fontWeight: 500,
    color: "#4a4a4a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    backgroundColor: "#C34A24",
    color: "#fff",
    border: "none",
    "&:hover": {
      backgroundColor: "#C34A24",
    },
  },
  "& .MuiPaginationItem-ellipsis": {
    border: "2px solid transparent",
    minWidth: 48,
    height: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    fontWeight: 500,
    color: "#4a4a4a",
  },
  "& .MuiPaginationItem-root.Mui-disabled": {
    border: "2px solid transparent",
    backgroundColor: "#f2f2f2",
    color: "#b0b0b0",
    pointerEvents: "none",
  },
}));
